// CIERRE DE SESIÓN POR INACTIVIDAD, POR EMPRESA · R-SEG3.
//
// El general de TEGO es el techo; una empresa de cara al público puede
// BAJAR su cierre por inactividad para lo suyo —un mostrador con una
// tableta compartida no debería quedar abierto media hora—. Nunca
// subirlo por encima del general: la política efectiva es el MENOR de los
// dos, y el servidor la impone; esta pantalla la fija y la explica.
import { useEffect, useState } from 'react';
import { Clock, ShieldCheck } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';

function Aviso({ tipo = 'ok', children }) {
  const clases = tipo === 'ok'
    ? 'text-green-800 bg-green-50 border-green-200'
    : tipo === 'error'
      ? 'text-red-700 bg-red-50 border-red-200'
      : 'text-amber-900 bg-amber-50 border-amber-200';
  return (
    <p role={tipo === 'error' ? 'alert' : 'status'}
       className={`text-sm border rounded-lg p-2.5 ${clases}`}>
      {children}
    </p>
  );
}

export default function CierreDeSesion() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  // 'general' = usar el techo de TEGO; 'propio' = un límite menor.
  const [modo, setModo] = useState('general');
  const [valor, setValor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/auth/inactividad-empresa')
      .then((d) => {
        if (!vivo) return;
        setDatos(d);
        setError(null);
        if (d.empresa_minutos != null) {
          setModo('propio');
          setValor(String(d.empresa_minutos));
        } else {
          setModo('general');
          setValor('');
        }
      })
      .catch((e) => {
        if (vivo) setError(e.message
          || 'No se pudo cargar este ajuste. Revisa la conexión y vuelve a intentarlo.');
      });
    return () => { vivo = false; };
  }, []);

  if (error) return <Aviso tipo="error">{error}</Aviso>;
  if (!datos) return <p className="text-sm text-gray-500">Cargando…</p>;

  const general = datos.general_minutos;

  // Validación EN VIVO, mientras se escribe, no al guardar.
  const n = parseInt(valor, 10);
  let motivo = null;                 // por qué NO se puede guardar todavía
  if (modo === 'propio') {
    if (valor.trim() === '' || Number.isNaN(n)) {
      motivo = 'Escribe cuántos minutos sin actividad esperar antes de cerrar.';
    } else if (n < 1) {
      motivo = 'Tiene que ser de al menos 1 minuto.';
    } else if (n > general) {
      motivo = `No puede pasar de ${general} minutos, que es el general de `
        + 'TEGO. Una empresa solo puede bajar el cierre, no subirlo.';
    }
  }

  // El valor que se mandaría: null para «usar el general».
  const aMandar = modo === 'general' ? null : n;
  const sinCambios = aMandar === (datos.empresa_minutos ?? null);
  const puedeGuardar = !motivo && !sinCambios && !guardando;

  // El que de verdad se aplicará tras guardar: el MENOR de los dos.
  const efectivoAhora = datos.efectivo_minutos;
  const efectivoTras = aMandar == null ? general : Math.min(general, aMandar);

  async function guardar() {
    setGuardando(true);
    setGuardado(null);
    setError(null);
    try {
      const d = await api.put('/auth/inactividad-empresa', { minutos: aMandar });
      setDatos(d);
      setGuardado(d.empresa_minutos == null
        ? `Listo. Esta empresa usa el general de TEGO: se cierra la sesión tras `
          + `${d.efectivo_minutos} minutos sin actividad.`
        : `Listo. En esta empresa se cierra la sesión tras ${d.efectivo_minutos} `
          + 'minutos sin actividad. Aplica la próxima vez que cada quien entre.');
    } catch (e) {
      setError(e.message
        || 'No se pudo guardar. Revisa la conexión y vuelve a intentarlo.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-[var(--color-marca)]" aria-hidden="true" />
          Cierre de sesión
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Tras cuánto tiempo sin actividad se cierra la sesión de esta empresa.
        </p>
      </div>

      <Ayuda id="seguridad.inactividad.pantalla" />

      <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        <p className="text-sm text-gray-700 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[var(--color-marca)] shrink-0"
                       aria-hidden="true" />
          Ahora se cierra tras <strong>{efectivoAhora} minutos</strong> sin
          actividad. El general de TEGO es <strong>{general} minutos</strong>.
        </p>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-gray-900 mb-1">
            ¿Cuánto para esta empresa?
          </legend>

          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="radio"
              name="modo-inactividad"
              className="mt-1"
              checked={modo === 'general'}
              onChange={() => { setModo('general'); setGuardado(null); }}
            />
            <span>
              Usar el general de TEGO
              <span className="text-gray-500"> ({general} minutos)</span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="radio"
              name="modo-inactividad"
              className="mt-1"
              checked={modo === 'propio'}
              onChange={() => { setModo('propio'); setGuardado(null); }}
            />
            <span>Poner un tiempo menor para esta empresa</span>
          </label>
        </fieldset>

        {modo === 'propio' && (
          <div className="pl-6 space-y-1">
            <label htmlFor="inactividad-minutos"
                   className="block text-sm text-gray-800">
              Cerrar la sesión tras
            </label>
            <div className="flex items-center gap-2">
              <input
                id="inactividad-minutos"
                type="number"
                inputMode="numeric"
                min="1"
                max={general}
                value={valor}
                onChange={(e) => { setValor(e.target.value); setGuardado(null); }}
                className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <span className="text-sm text-gray-600">minutos sin actividad</span>
            </div>
            <Ayuda id="seguridad.inactividad.minutos" />
            {motivo && <p className="text-xs text-red-700 mt-1">{motivo}</p>}
            {!motivo && (
              <p className="text-xs text-gray-500 mt-1">
                Se cerrará tras <strong>{efectivoTras} minutos</strong> sin
                actividad.
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={guardar}
            disabled={!puedeGuardar}
            className="self-start px-4 py-2 text-sm rounded-lg bg-[var(--color-marca)]
                       text-white font-semibold disabled:opacity-50
                       disabled:cursor-not-allowed"
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          {/* El botón deshabilitado dice por qué, a la vista. */}
          {!puedeGuardar && !guardando && (
            <p className="text-xs text-gray-500">
              {motivo || (sinCambios ? 'No hay cambios que guardar.' : '')}
            </p>
          )}
        </div>

        {guardado && <Aviso tipo="ok">{guardado}</Aviso>}
      </section>
    </div>
  );
}
